/**
 * Admin screen to view ledgers for an account
 */
import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import LoadingSpinner from '../ui/LoadingSpinner';
import PageTitle from '../ui/PageTitle';
import ServerError from '../ui/ServerError';
import { FormGroup, FormControlLabel, Switch,
         Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import AppBindStyles from '../../AppBindStyles';

const abEnvironment = new AbEnvironment();
const AccountLedgersQuery = graphql`
query AccountLedgersQuery($accountid: UUID, $mode: Int, $first: Int, $offset: Int) {
  getledgersbyaccount(accountid: $accountid, mode: $mode, first: $first, offset: $offset) {
    totalCount
    nodes {
      ledgerId
      ledgerName
      balanceType
      ledgerType
      ledgerDescription
      debitBalance
      creditBalance
    }
  }
}
`;

const styleSheet = theme => ({
    dateField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 4,
      width: 180,
    },
    textField2: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: 3,
      width: 160,
    },
    textFieldSearch: {
      marginRight: theme.spacing(1),
      marginTop: 3,
      marginLeft: 0,
      width: 300,
    }
});

class AccountLedgers extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: 1,
      page: 0,
      rowsPerPage: 10,
    };  
  }

  goTo(url) {
    if (url == null)
      return;
      
    this.props.history.push(`${url}`);
  }        

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  render() {
    const { isAuthenticatedAppBind, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticatedAppBind()) { 
      return ( <Redirect to='/home' /> );
    }    

    const params = new URLSearchParams(this.props.location.search);
    let acctId = params.get('accountId');
    let acctName = params.get('accountName');   
    
    //let mode = 0;

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let totalCount = 0;

    let theTitle = 'Account ledgers for ' + acctName;
    return (
      <React.Fragment>
        <PageTitle title={theTitle}/>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={this.state.mode === 0}
                onChange={(e, checked) => {
                  if (checked) {
                    this.setState({ mode: 0, page: 0});
                  } else {
                    this.setState({ mode: 1, page: 0});
                  }
                }}
              />
              }
            label="Only show ledgers with a non-zero balance"
          />
        </FormGroup>

        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={AccountLedgersQuery}
          variables={{accountid: acctId,
                      mode: this.state.mode,
                      first: first,
                      offset: offset}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // Determine offset information
              totalCount = props.getledgersbyaccount.totalCount;

              // Get sums on page
              let debitTtl = 0;
              let creditTtl = 0;
              for (let i=0; i<props.getledgersbyaccount.nodes.length; i++) {
                let n = props.getledgersbyaccount.nodes[i];
                debitTtl = debitTtl + Number(n.debitBalance);
                creditTtl = creditTtl + Number(n.creditBalance);
              }

              return (
                <React.Fragment>
                  <Typography variant="body1">Debit TTL: {debitTtl}, Credit TTL: {creditTtl}</Typography>
                  <div>
                    <Table>
                      <TableHead className={classes.tblHead}>
                        <TableRow className={classes.tblHeadRow}>
                          <TableCell className={classes.tblHeadCell}>Ledger</TableCell>
                          <TableCell className={classes.tblHeadCell}>Type</TableCell>
                          <TableCell className={classes.tblHeadCell}>Description</TableCell>
                          <TableCell colSpan={2} className={classes.tblHeadCell}>Balance</TableCell>
                        </TableRow>
                        <TableRow className={classes.tblHeadRow2}>
                          <TableCell className={classes.tblHeadCell} colSpan={3}/>
                          <TableCell className={classes.tblHeadCell2}>Debit</TableCell>
                          <TableCell className={classes.tblHeadCell2}>Credit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { props.getledgersbyaccount.nodes.map(n => {
                          let ledgerLink = "ledger?ledgerId=" + n.ledgerId + '&ledgerName=' + n.ledgerName + '&accountName=' + acctName;

                          return (
                            <TableRow key={n.ledgerId}>
                              <TableCell onClick={() => {this.goTo(ledgerLink);}}>{n.ledgerName}</TableCell>
                              <TableCell>{n.ledgerType}</TableCell>
                              <TableCell>{n.ledgerDescription}</TableCell>
                              <TableCell align="right">{n.debitBalance}</TableCell>
                              <TableCell align="right">{n.creditBalance}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <div style={AppBindStyles.paginationTableFooter}>
                    <TablePagination
                      rowsPerPageOptions={[10, 50, 100]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={this.state.rowsPerPage}
                      page={this.state.page}
                      backIconButtonProps={{
                        'aria-label': 'Previous Page',
                      }}
                      nextIconButtonProps={{
                        'aria-label': 'Next Page',
                      }}
                      onPageChange={this.handleChangePage}
                      onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />                        
                  </div>
                </React.Fragment>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      </React.Fragment>
    );
  }
}
  
AccountLedgers.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(AccountLedgers);

